// PURPOSE: Application help page — overview, cloud hosting context,
//          and page-by-page operating instructions.

const Help = () => {

	const fs = {
		marginBottom: "1.25rem",
	}

	const p = {
		marginBottom: "0.6rem",
		lineHeight: "1.6",
	}

	const h4 = {
		marginBottom: "0.4rem",
		marginTop: "0.75rem",
		color: "#d4d4d4",
	}

	const ul = {
		paddingLeft: "1.4rem",
		marginBottom: "0.6rem",
		lineHeight: "1.6",
	}

	const kbd = {
		background: "#444",
		color: "#eee",
		border: "1px solid #666",
		borderRadius: "3px",
		padding: "1px 5px",
		fontSize: "0.85em",
		fontFamily: "monospace",
	}

	return (
		<main style={{ width: "100%", maxWidth: "860px", margin: "0 auto", padding: "1rem 1.5rem", height: "calc(100vh - 9rem)", overflowY: "auto", boxSizing: "border-box", textAlign: "left" }}>

			{/* ── Welcome ──────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Welcome to CR Cloud Technology</legend>
				<p style={p}>
					Thank you for using <strong>CR Cloud Technology</strong> — a cloud-based government services platform
					designed to help county assessor offices manage dog licensing, kennel registrations, tag inventory,
					staff access, fee schedules, farm use applications, and reporting from a single, secure application.
				</p>
				<p style={p}>
					This help page provides an overview of the hosting environment and step-by-step instructions
					for every section of the application. Use the navigation bar at the top of the screen to move
					between modules at any time.
				</p>
				<h4 style={h4}>Field-level help</h4>
				<p style={p}>
					Input fields highlighted with a <strong style={{ backgroundColor: "lightgreen", color: "#333", padding: "0 4px", borderRadius: "2px" }}>light green background</strong> support
					context-sensitive help. Press <kbd style={kbd}>F1</kbd> while the cursor is in one of these
					fields to open a detailed help panel for that field.
				</p>
			</fieldset>

			{/* ── Cloud Hosting ─────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Cloud Hosting — Amazon Web Services (AWS)</legend>
				<p style={p}>
					CR Cloud Technology is hosted entirely on <strong>Amazon Web Services (AWS)</strong>, the world's
					leading cloud infrastructure provider. All application servers, databases, and file storage
					run within AWS data centers, which means you do not need to install or maintain any server
					software on your own premises.
				</p>
				<h4 style={h4}>Key advantages of cloud deployment</h4>
				<ul style={ul}>
					<li><strong>Availability</strong> — the application is accessible from any computer with
					a modern browser and an internet connection, including from home or in the field.</li>
					<li><strong>Automatic backups</strong> — your data is backed up continuously by AWS managed
					database services. Point-in-time recovery is available without manual intervention.</li>
					<li><strong>Security</strong> — all data is encrypted in transit (HTTPS) and at rest.
					AWS maintains physical security, network firewalls, and compliance certifications on
					your behalf.</li>
					<li><strong>Scalability</strong> — server capacity scales automatically to handle busy
					periods such as the annual license renewal season without performance degradation.</li>
					<li><strong>Disaster recovery</strong> — because data lives in the cloud rather than on a
					local server, a hardware failure, fire, or flood at your office does not result in data
					loss.</li>
					<li><strong>Always current</strong> — software updates are deployed centrally by CR Cloud
					Technology. You receive new features and security patches automatically with no action
					required on your part.</li>
				</ul>
			</fieldset>

			{/* ── Your Responsibilities ─────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Your Desktop and Browser Responsibilities</legend>
				<p style={p}>
					CR Cloud Technology runs entirely within your web browser — there is nothing to install on your
					computer. However, the security and performance of your session depend on the condition of
					your local environment.
				</p>
				<ul style={ul}>
					<li><strong>Keep your operating system up to date.</strong> Apply Windows, macOS, or other
					OS security updates promptly. An unpatched OS can expose your login session to
					local threats regardless of how secure the server is.</li>
					<li><strong>Keep your browser up to date.</strong> CR Cloud Technology is tested on current
					versions of Chrome, Edge, and Firefox. Outdated browsers may display pages incorrectly
					or lack security features required for a safe connection.</li>
					<li><strong>Use a supported browser.</strong> Internet Explorer is not supported. Use
					Google Chrome, Microsoft Edge, or Mozilla Firefox.</li>
					<li><strong>Do not share your login credentials.</strong> Each staff member should have
					their own account. Shared credentials make audit logs unreliable and increase risk if
					a password is compromised.</li>
					<li><strong>Log out when finished.</strong> Use the Logout link in the navigation bar
					at the end of every session, especially on shared computers.</li>
				</ul>
			</fieldset>

			{/* ── Login ─────────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Login Page</legend>
				<p style={p}>
					The Login page is the starting point for all users. Enter your registered email address
					and password, then click <strong>Sign In</strong>.
				</p>
				<h4 style={h4}>Session timeout</h4>
				<p style={p}>
					For security, your session will time out after a period of inactivity defined by your
					administrator in the Configure module. You will be returned to the Login page automatically.
					Any unsaved data will be lost, so save your work before stepping away.
				</p>
				<h4 style={h4}>Forgotten password</h4>
				<p style={p}>
					Enter your email address on the Login page, then click <strong>Forgot Password</strong>.
					A reset link will be sent to that address. Follow the instructions in the email to set a new password.
				</p>
				<h4 style={h4}>New account activation</h4>
				<p style={p}>
					New accounts are created by an administrator using your email address. By design, new accounts
					do not have a starting password. To activate your account, enter your email address on the
					Login page and click <strong>Forgot Password</strong> — this will send a password-setup link
					to your email address, allowing you to establish your own private password.
				</p>
			</fieldset>

			{/* ── Configure ─────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Configure</legend>
				<h4 style={h4}>Display theme</h4>
				<p style={p}>
					Choose from six colour themes: Light Grey Blue, Dark Grey Blue, Light Green, Dark Green, White, and Black.
					Select a radio button to apply the theme — it takes effect <strong>immediately</strong> with no save button required.
					The theme is personal to your login account and does not affect other users.
				</p>
				<h4 style={h4}>Idle timeout</h4>
				<p style={p}>
					The idle timeout is a <strong>global setting</strong> that applies to every user in the system.
					The timeout input has a <strong style={{ backgroundColor: "lightgreen", color: "#333", padding: "0 4px", borderRadius: "2px" }}>light green background</strong> — press <kbd style={kbd}>F1</kbd> while in it for full instructions.
					Enter the number of minutes of inactivity (5 – 60) before a user is automatically logged out,
					then press <strong>Tab</strong> or <strong>Enter</strong> to save. Any activity — keystrokes,
					mouse movement, or clicks — resets the timer. When the timer expires the user is returned to
					the Login page and any unsaved work is lost.
				</p>
				<h4 style={h4}>Maintenance</h4>
				<p style={p}>
					The <strong>Maintenance</strong> section — available to users with Staff (User module)
					access — permanently removes old records to keep the system tidy. Each action shows a
					live count of eligible records and is disabled when the count is zero:
				</p>
				<ul style={ul}>
					<li><strong>Delete Old Farm Applications</strong> — removes farm applications from
					before the cutoff year.</li>
					<li><strong>Delete Old DMV Records</strong> — removes imported DMV records from before
					the cutoff year.</li>
					<li><strong>Delete Unused Prior-Year Tags</strong> — removes generated-but-unused dog
					tags from before the current fiscal year.</li>
				</ul>
				<p style={p}>
					Each action opens a <strong>Confirm Deletion</strong> prompt showing how many records
					will be removed. Click <strong>Delete</strong> to proceed or <strong>Cancel</strong> to
					stop. <strong>These deletions cannot be undone.</strong>
				</p>
			</fieldset>

			{/* ── Tag Setup ─────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Tag Setup</legend>
				<p style={p}>
					The Tag Setup menu contains three sub-pages used to prepare the annual tag inventory before
					the licensing season begins.
				</p>

				<fieldset style={{ ...fs }}>
					<legend>Fees</legend>
					<p style={p}>
						The Fees page defines the dollar amounts charged for each license type for the current
						year. All amounts are entered in dollars and cents (e.g. <em>$3.00</em>).
					</p>
					<ul style={ul}>
						<li><strong>Dog</strong> — standard annual dog license fee.</li>
						<li><strong>Sheep / Goat</strong> — per-head livestock license fee.</li>
						<li><strong>Kennel</strong> — fee charged per kennel registration. Each registration
						entitles the owner to five dog tags at no additional cost.</li>
						<li><strong>Service Dog</strong> — no fee is charged; enter $0.00.</li>
						<li><strong>Vicious</strong> — surcharge applied in addition to the standard dog fee when
						a dog is designated vicious.</li>
						<li><strong>Late Filing</strong> — penalty for licenses obtained after the deadline.</li>
						<li><strong>Lost Tag / Replacement</strong> — fee for issuing a replacement tag.</li>
						<li><strong>Returned Check</strong> — fee charged when a check payment is returned.</li>
						<li><strong>Impound</strong> — fee for impounded animals.</li>
					</ul>
					<p style={p}>
						Click <strong>Save</strong> after making changes. The fee schedule takes effect for
						all new transactions immediately.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Assign Staff</legend>
					<p style={p}>
						Deputies (field staff who issue tags) must be assigned to the tag system before they
						can receive tag inventory. Use this page to link a staff member to their deputy record.
						Select the staff member from the list and click <strong>Assign</strong>.
					</p>
				</fieldset>

				<fieldset style={{ ...fs, marginBottom: 0 }}>
					<legend>Tag Management</legend>
					<p style={p}>
						Tag Management controls the lifecycle of physical dog tags. Tags progress through the
						following states: <em>Generated → Assigned → Active / Kennel / Service Dog → Replacement</em>.
					</p>
					<p style={p}>
						Tag Setup is locked to the current fiscal year (<strong>July 1 – June 30</strong>). The
						fiscal year is shown as a read-only <strong>Fiscal Year:</strong> field (for example
						<em> July 1, 2026 – June 30, 2027</em>) and cannot be changed, so tags can never be
						generated or assigned for the wrong year.
					</p>
					<p style={p}>
						When unused tags remain from a prior fiscal year, a
						<strong style={{ color: "#ffd700" }}> Maintenance Due on old fiscal year</strong> notice
						appears. Those tags can be cleared from Configure → Maintenance.
					</p>
					<ul style={ul}>
						<li><strong>Generate Tags</strong> — creates a numbered range of tags for the current
						year. Enter the quantity of tags needed, then click <strong>Load Tags</strong>.
						The starting tag number defaults to 1. Only set a different starting number when generating
						additional tags for the same year — for example, if the initial supply runs out and a new
						batch is needed mid-season.</li>
						<li><strong>Assign to Deputy</strong> — allocates packets of generated tags to deputies.
						Use <strong>Auto-Assign</strong> to automatically name and assign one packet to each deputy
						in a single click; pressing it again assigns another round. You can also assign or unassign
						individual packets to a selected deputy. All assignments are drawn from the unassigned
						inventory.</li>
						<li><strong>Tag Inventory</strong> — displays the current status of every tag for
						the selected year. Use the status filter to view Unassigned, Assigned, Active, Kennel,
						Service Dog, or Replacement tags.</li>
					</ul>
				</fieldset>
			</fieldset>

			{/* ── License ───────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>License</legend>
				<p style={p}>
					The License menu is the primary day-to-day work area. The License Search page brings
					together six fieldsets on a single screen — Search, Taxpayer Information, Sheep &amp; Goats,
					Dogs on File, Dog Information, and Receipt — so that everything needed to look up a taxpayer,
					manage their animals, and complete a transaction is visible and accessible without navigating
					away from the page.
				</p>

				<fieldset style={{ ...fs }}>
					<legend>License Search</legend>
					<p style={p}>
						Use this page to look up existing taxpayers, add new taxpayers, manage their dogs,
						issue kennel registrations, record livestock counts, and generate receipts. Incorrect
						entries in required fields are indicated by a <strong style={{ color: "red" }}>red border</strong>.
						The border clears automatically once the entry is valid.
					</p>

					<h4 style={h4}>Taxpayer Search fieldset</h4>
					<p style={p}>
						Type one or more characters in the <strong>Search By</strong> field and press <kbd style={kbd}>Enter</kbd>
						to produce a matching list of taxpayers. Click any row in the list to load that taxpayer.
						The Search By field has a <strong style={{ backgroundColor: "lightgreen", color: "#333", padding: "0 4px", borderRadius: "2px" }}>light green background</strong> — press <kbd style={kbd}>F1</kbd> while in it for detailed search instructions.
						Click <strong>Clear Page</strong> to reset all fields and begin a new transaction.
					</p>
					<p style={p}>
						The field works out what you meant: digits are a tag number, ten digits a phone
						number, anything containing an @ an email address, and anything else a name.
						Names match in any order and on partial words, so <em>smi joh</em> finds
						John Smith. Nicknames are matched too — searching <em>Bob</em> finds a
						taxpayer recorded as <em>Robert</em>.
					</p>

					<h4 style={h4}>Searching by tag number</h4>
					<p style={p}>
						A tag number search looks at <strong>every tag year</strong>, not just the
						current one. Tag numbers are reissued to a different dog each year, so the
						same number can belong to several owners over time — searching it shows each
						of them.
					</p>
					<p style={p}>
						If only one year used that number the taxpayer loads straight away, with the
						dog that held the tag selected. If more than one did, a selection list appears
						with the <strong>Year</strong> as its first column, newest first; click the row
						you want. The same owner can appear twice, because an owner licensing early
						each year may draw the same number again — the year and the dog tell the rows
						apart.
					</p>

					<h4 style={h4}>Taxpayer Information fieldset</h4>
					<p style={p}>
						When a taxpayer is found their information is loaded automatically. To add a new
						taxpayer, first click <strong>Clear Page</strong> to ensure the form is empty, then
						fill in the required fields and click <strong>Add Taxpayer</strong>. To update an
						existing taxpayer's information, edit the fields and click <strong>Save</strong>.
						Address changes trigger automatic USPS address verification; if no match is found the
						entered address is accepted as-is.
					</p>
					<p style={p}>
						For kennel owners, enter the <strong>Kennel Name</strong> in the field provided. The
						kennel name saves automatically when the field loses focus — no Save button is required.
						The <strong>Add Kennel Registration</strong> button appears below the kennel name field.
						Click it to register a kennel for the current taxpayer (see Kennel Registration below).
					</p>

					<h4 style={h4}>Dog Information fieldset</h4>
					<p style={p}>
						Complete the dog's details and click <strong>Add Dog</strong> to add a new dog to
						the current taxpayer. Click any row in the <strong>Dogs on File</strong> table to
						select an existing dog for editing, then click <strong>Update Dog</strong> to save
						changes. The Breed and Tag# Override fields have a <strong style={{ backgroundColor: "lightgreen", color: "#333", padding: "0 4px", borderRadius: "2px" }}>light green background</strong> — press <kbd style={kbd}>F1</kbd> in either field for
						field-specific guidance.
					</p>
					<ul style={ul}>
						<li><strong>Tag Status</strong> — Active (standard fee), Service Dog (exempt), or
						Replacement (lost tag). Kennel status is assigned automatically through the kennel
						registration process and cannot be set manually.</li>
						<li><strong>Vicious</strong> — checking this box adds a vicious dog surcharge to
						the receipt in addition to the standard license fee.</li>
						<li><strong>Tag# Override</strong> — optionally specify a particular tag number
						instead of automatic assignment. The tag must be in Assigned or Unassigned status.
						Assignment does not occur until Save &amp; Print is pressed.</li>
						<li><strong>Clear</strong> — resets the dog fields and returns to add mode.</li>
					</ul>

					<h4 style={h4}>Kennel Registration</h4>
					<p style={p}>
						A kennel registration entitles the owner to five physical dog tags at no charge per
						registration, plus one kennel registration fee. Multiple registrations are permitted —
						each adds five more tags at the kennel fee.
					</p>
					<p style={p}>
						To register a kennel: enter the <strong>Kennel Name</strong> in the Taxpayer Information
						fieldset, then click <strong>Add Kennel Registration</strong>. The system will:
					</p>
					<ul style={ul}>
						<li>Charge one kennel registration fee (e.g. R1, R2 for subsequent registrations).</li>
						<li>Fill available slots with any existing untagged dogs belonging to the taxpayer,
						converting their status to Kennel at no charge.</li>
						<li>Create placeholder dog records (named <em>R1-D1</em>, <em>R1-D2</em>, etc.) for
						any remaining slots. These represent tags issued to the kennel owner for dogs not yet
						individually registered.</li>
						<li>Print a <strong>Kennel Certificate</strong> alongside the receipt at Save &amp; Print.</li>
					</ul>
					<p style={p}>
						If a caller identifies a lost dog by tag number, the assessor can look up the kennel
						owner by that tag number. Kennel dogs may be individually named by selecting the
						placeholder row and updating the dog name in the Dog Information fieldset.
					</p>

					<h4 style={h4}>Dogs on File fieldset</h4>
					<p style={p}>
						Lists all dogs registered to the current taxpayer. Click a row to load that dog's
						details into the Dog Information fieldset. Any dog may be deleted using the
						<strong> Delete</strong> button — for example if the owner no longer has the animal.
						Deleting a dog added during the current session also removes it from the receipt.
						Deleting a previously existing dog does not affect the receipt.
					</p>
					<p style={p}>
						A dog name shown in <strong style={{ color: "red" }}>red</strong> indicates the dog
						was licensed in a prior year and has not yet been issued a current-year tag — no tag
						number is displayed for these dogs. Press <strong>Issue New Tags</strong> to queue
						those dogs for new current-year tags. The new tag numbers are assigned when <strong>Save &amp; Print</strong> is pressed.
					</p>

					<h4 style={h4}>Sheep &amp; Goats fieldset</h4>
					<p style={p}>
						Enter the number of sheep and goats owned by the taxpayer for the current year and
						click <strong>Save</strong>. The Sheep and Goats count fields have a <strong style={{ backgroundColor: "lightgreen", color: "#333", padding: "0 4px", borderRadius: "2px" }}>light green background</strong> — press <kbd style={kbd}>F1</kbd> in either field for full instructions. Once saved for the year the counts become read-only.
						The livestock fee is added to the receipt automatically and included in the
						Save &amp; Print transaction.
					</p>

					<h4 style={h4}>Receipt fieldset</h4>
					<p style={p}>
						The receipt is built automatically as dogs, kennel registrations, and livestock are
						added. Select <strong>Cash</strong> or <strong>Check</strong> as the payment method.
						If Check is selected, enter the check number before printing. The receipt number is
						assigned automatically. Click <strong>Save &amp; Print</strong> to record all
						transactions, assign tag numbers to new dogs, print the receipt, and — for kennel
						registrations — print the kennel certificate. The page clears automatically after
						printing, ready for the next taxpayer.
					</p>
				</fieldset>

				<fieldset style={{ ...fs, marginBottom: 0 }}>
					<legend>Duplicates</legend>
					<p style={p}>
						The same person is sometimes entered twice — usually because the address on file is
						out of date, so a search does not find them and a second record is created. The
						Duplicates page lists records that look like the same person and lets you combine
						them. Combining moves every dog, tag and payment onto the record you keep;
						nothing is discarded, and any combine can be undone.
					</p>

					<h4 style={h4}>Reviewing a proposed pair</h4>
					<p style={p}>
						Each entry in the <strong>Pending</strong> list shows the two records side by side,
						labelled <strong>Newest record</strong> and <strong>Older record</strong>, with each
						one's address and what is on file for it — the number of dogs and payments. Use those
						details to decide whether they really are the same person. The coloured note above
						each pair explains why it was proposed.
					</p>
					<p style={p}>
						Use the <strong>Show</strong> options to filter the list. <strong>Needs a decision</strong>
						is the normal working list. <strong>Possible double payment</strong> flags pairs where
						both records were paid in the same tax year — those may need a refund and should be
						settled before combining. <strong>Clear duplicates</strong> are pairs where the older
						record has no dogs, tags or payments at all.
					</p>

					<h4 style={h4}>Combining or dismissing</h4>
					<p style={p}>
						Click <strong>Combine these</strong> to merge the pair. Everything attached to the
						older record moves to the record being kept, and the emptied record is removed.
						Click <strong>Different people</strong> if they are two genuinely different people
						who happen to share a name — the pair is remembered and will not be proposed again.
					</p>

					<h4 style={h4}>Undoing a combine</h4>
					<p style={p}>
						The <strong>Recent combines</strong> list beside the pending queue shows what has
						been combined, most recent first. Click <strong>Undo</strong> to reverse one. The
						removed record is restored exactly as it was, and only the dogs, tags and payments
						that moved during that combine are sent back — anything added since stays with the
						record you kept. An undone combine is marked <strong>Undone</strong> and the pair is
						treated as two different people from then on.
					</p>

					<h4 style={h4}>How duplicates are prevented</h4>
					<p style={p}>
						When you add a taxpayer whose first name, last name and city match a record that
						already exists, a warning lists the matching records with their addresses so you can
						use the existing one instead. If it really is a different person, choose
						<strong> Add anyway</strong> — the new record is created and that pair is remembered,
						so it is never raised on this page.
					</p>
				</fieldset>

			</fieldset>

			{/* ── DMV ───────────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>DMV</legend>
				<p style={p}>
					The DMV module lets you look up motor-vehicle, boat, and mobile-home registration
					records imported from the state DMV. It is available to users who have been granted
					DMV module access.
				</p>

				<fieldset style={{ ...fs, marginBottom: 0 }}>
					<legend>DMV Registration Search</legend>
					<p style={p}>
						Build a search from up to three criteria. Each search row has a dropdown to choose
						the field and a box to type the value. The available fields are <strong>Owner Name</strong>,
						<strong> Street Address</strong>, <strong>Data Year</strong>, <strong>Model Year</strong>,
						<strong> Make</strong>, and <strong>VIN / HIN</strong>. A field can only be used once.
						Street Address matches the street portion only; Data Year and Model Year must be
						whole numbers.
					</p>
					<h4 style={h4}>Search Type</h4>
					<ul style={ul}>
						<li><strong>Contains</strong> — finds records where your text appears anywhere within
						the field (a case-insensitive substring match).</li>
						<li><strong>Similar</strong> — a typo-tolerant match that also finds close spellings
						(for example <em>SMYTH</em> will find <em>SMITH</em>), with the closest matches
						ranked first.</li>
					</ul>
					<p style={p}>
						Click <strong>Search</strong> to run the query or <strong>Clear</strong> to reset the
						form. The results table shows <strong>Year</strong>, <strong>Type</strong> (Vehicle,
						Boat, or Mobile Home), <strong>Owner Name</strong>, <strong>Address</strong>,
						<strong> City</strong>, <strong>Model Yr</strong>, <strong>Make</strong>, and
						<strong> VIN / HIN</strong>. A maximum of 500 records is returned; if the results are
						capped, refine your search to narrow them.
					</p>
					<p style={p}>
						Click any result row to open the <strong>DMV Record</strong> window, which shows the
						full record — asset type, owner and address, VIN / HIN, make and model year, body/hull,
						weight/length, title and plate details, and multi-year status. Click <strong>Close</strong>
						to return to the results.
					</p>
				</fieldset>
			</fieldset>

			{/* ── Dashboard ─────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Dashboard</legend>
				<p style={p}>
					The Dashboard presents at-a-glance summaries for your office. It is available to users
					who have been granted Dashboard access.
				</p>
				<h4 style={h4}>Deputy Ranking</h4>
				<p style={p}>
					The <strong>Deputy Ranking</strong> panel ranks deputies by the taxes they have collected
					during the current <strong>calendar year</strong> (January 1 – December 31); the heading
					shows the year (for example <em>Deputy Ranking 2026</em>). Columns include <strong>Deputy</strong>,
					<strong> Taxpayers</strong>, <strong>Email</strong>, <strong>Phone</strong>, <strong>Dogs</strong>,
					<strong> Sheep</strong>, <strong>Goats</strong>, and <strong>Taxes</strong>, with a
					<strong> Total</strong> row at the bottom. The panel loads automatically — no parameters
					are required.
				</p>
			</fieldset>

			{/* ── Staff ─────────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Staff</legend>
				<p style={p}>
					The Staff menu is available to users who have been granted User module access. It provides
					tools for managing the staff accounts that can log in to CR Cloud Technology.
				</p>

				<fieldset style={{ ...fs }}>
					<legend>Add User</legend>
					<p style={p}>
						Complete the new staff member's last name, first name, email address, and mobile phone
						number, then click <strong>Add User</strong>. The system will send the new user an
						activation email containing a link and a verification code. The account remains in
						<em> Pending</em> status until the user completes activation.
					</p>
					<p style={p}>
						Email addresses and phone numbers must be unique across all CR Cloud Technology clients.
						An error message will appear if a duplicate is detected.
					</p>
				</fieldset>

				<fieldset style={{ ...fs, marginBottom: 0 }}>
					<legend>List Users</legend>
					<p style={p}>
						Displays all staff members for your organisation. From this list you can:
					</p>
					<ul style={ul}>
						<li><strong>Edit</strong> — update a staff member's name, email, or phone. Changing
						the email address will suspend the account until the new address is verified.</li>
						<li><strong>Modules</strong> — grant or revoke access to individual application
						modules for the selected staff member. Check the box next to a module to grant
						access; uncheck to revoke.</li>
					</ul>
					<p style={p}>
						The Edit and Modules buttons are disabled for your own account and for accounts in
						Pending status. The initial system user's module access is fixed and cannot be
						changed.
					</p>
				</fieldset>
			</fieldset>

			{/* ── Farm ─────────────────────────────────────────────────────────── */}
			<fieldset style={fs}>
				<legend>Farm</legend>
				<p style={p}>
					The Farm module records annual farm-use applications submitted by landowners seeking
					agricultural classification for their property. West Virginia's fiscal year runs
					<strong> July 1 – June 30</strong>. Applications are collected each year and must
					be completed before the September 1 deadline.
				</p>

				<fieldset style={{ ...fs }}>
					<legend>Farm Owner Search</legend>
					<p style={p}>
						Type any part of the owner name (the search matches anywhere in the name), or an
						account ID, and press <kbd style={kbd}>Enter</kbd> or
						click <strong>Search</strong> to locate an existing farm owner. Select the target
						<strong> Fiscal Year</strong> from the dropdown before searching — this controls which
						application year is loaded when you select an owner.
					</p>
					<p style={p}>
						Click <strong>Add Owner</strong> to create a new farm owner record. Click any row
						in the search results to load that owner's details and applications.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Owner Information</legend>
					<p style={p}>
						Displays the selected owner's name, mailing address, phone, and email. Click
						<strong> Edit</strong> to update any field, then <strong>Save</strong> to commit.
						Address changes are verified against the USPS database automatically; if no
						match is found the entered address is accepted as-is.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Applications</legend>
					<p style={p}>
						Lists all farm applications on file for the selected owner. Each row shows the
						fiscal year and the operation name. Click a row to load that application.
					</p>
					<h4 style={h4}>New application</h4>
					<p style={p}>
						Click <strong>New Application</strong> to create an application for the currently
						selected fiscal year. The application opens with the owner's information
						pre-populated. Fill in the operation details, then work through each section tab.
					</p>
					<h4 style={h4}>Roll Forward</h4>
					<p style={p}>
						<strong>Roll Forward</strong> copies the previous year's completed application into
						the current fiscal year, preserving all crop, livestock, tree, production, parcel,
						and qualification data. The operator then reviews and updates each section for the
						new year. Roll Forward will only proceed if no application already exists for the
						target year; if one does, a confirmation prompt asks whether to overwrite it.
					</p>
					<p style={p}>
						After a roll forward, the section progress dots turn <strong style={{ color: "#ffd700" }}>yellow</strong> to
						indicate that each section contains carried-over data that has not yet been reviewed
						for the new year. Save a section to confirm it — the dot turns <strong style={{ color: "#90ee90" }}>green</strong>.
						When all sections have been reviewed, a <strong>Farm Application Review Complete</strong> message
						appears briefly. Sections with no data show a <strong style={{ color: "gray" }}>grey</strong> dot
						and require no action.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Farm Operation</legend>
					<p style={p}>
						Records the basic details of the farming operation:
					</p>
					<ul style={ul}>
						<li><strong>Operation Name</strong> — the name under which the farm operates.</li>
						<li><strong>Acres Owned / Acres Rented</strong> — total acreage owned and rented
						by the operator for this fiscal year.</li>
						<li><strong>Veteran Farmer</strong> — check if the operator qualifies as a veteran
						farmer under WV code.</li>
					</ul>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Operator Information</legend>
					<p style={p}>
						The operator is the person or entity who actually works the farm. If the operator
						is the same as the owner, check <strong>Same as Owner</strong> and the fields
						are filled automatically. Otherwise, enter the operator's name, address, phone,
						and email separately.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Crops</legend>
					<p style={p}>
						Record the acreage and production for each crop category grown during the fiscal year.
					</p>
					<ul style={ul}>
						<li><strong>Field Crops</strong> — corn, soybeans, wheat, oats, barley, rye, sorghum,
						tobacco, and other standard row crops. Enter acres and units of production for each.</li>
						<li><strong>Hay / Haylage / Pasture</strong> — enter acres and tons for each
						cutting of hay or haylage, plus total pasture acres.</li>
					</ul>
					<p style={p}>
						Click <strong>Save Crops</strong> after making changes.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Trees &amp; Specialty</legend>
					<p style={p}>
						Record acreage and production for orchard fruits, sap trees, vegetables, and
						controlled-environment growing:
					</p>
					<ul style={ul}>
						<li><strong>Fruit Trees / Orchards</strong> — acres and bushels by fruit type.</li>
						<li><strong>Sap Trees</strong> — number of taps and gallons of sap collected.</li>
						<li><strong>Vegetables</strong> — acres and value of vegetable production.</li>
						<li><strong>High Tunnels / Greenhouses</strong> — number of structures and
						square footage.</li>
					</ul>
					<p style={p}>
						Click <strong>Save Trees</strong> after making changes.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Livestock</legend>
					<p style={p}>
						Record the number of animals in each livestock category as of the application date.
					</p>
					<ul style={ul}>
						<li><strong>Cattle</strong> — beef cows, dairy cows, bulls, steers, heifers, and calves.</li>
						<li><strong>Sheep &amp; Goats</strong> — ewes, rams, does, bucks, lambs, and kids.</li>
						<li><strong>Hogs</strong> — breeding sows, boars, and market hogs.</li>
						<li><strong>Equine</strong> — horses, ponies, mules, and donkeys.</li>
						<li><strong>Poultry</strong> — chickens, turkeys, ducks, geese, and other birds,
						recorded as the average flock size for the year.</li>
						<li><strong>Bees</strong> — number of hives and pounds of honey produced.</li>
						<li><strong>Other Animals</strong> — any livestock not covered by the above
						categories (e.g. llamas, emus).</li>
					</ul>
					<p style={p}>
						Click <strong>Save Livestock</strong> after making changes.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Production Values</legend>
					<p style={p}>
						Enter the total gross dollar value of farm production for the fiscal year:
					</p>
					<ul style={ul}>
						<li><strong>Crop Production Value</strong> — total value of all crops sold or consumed.</li>
						<li><strong>Livestock Production Value</strong> — total value of animals and animal
						products sold.</li>
						<li><strong>Agri-Tourism Value</strong> — income from farm-based tourism activities
						such as corn mazes, pick-your-own operations, or farm stays.</li>
					</ul>
					<p style={p}>
						Click <strong>Save Production</strong> after making changes. The total of these values
						is what decides whether the $1,000 production threshold in <strong>Qualifications</strong>
						is met, and it seeds the suggested <strong>Farm Income</strong> figure there.
					</p>
				</fieldset>

				<fieldset style={{ ...fs }}>
					<legend>Parcels</legend>
					<p style={p}>
						Lists the individual tax parcels that make up the farm. Each parcel requires:
					</p>
					<ul style={ul}>
						<li><strong>Tax District</strong> — select from the county district list.</li>
						<li><strong>Map / Parcel Code</strong> — the county parcel identification number.</li>
						<li><strong>Land Use Acres</strong> — acreage by use category (cropland, pasture,
						woodland, wasteland, homesite, etc.) for that parcel.</li>
					</ul>
					<p style={p}>
						Click <strong>Add Parcel</strong> to add a new row. Click <strong>Save Parcels</strong>
						to save all parcel entries. All parcels are saved together in a single operation.
					</p>
				</fieldset>

				<fieldset style={{ ...fs, marginBottom: 0 }}>
					<legend>Qualifications</legend>
					<p style={p}>
						Records the answers to eligibility questions required by WV code:
					</p>
					<ul style={ul}>
						<li><strong>Items A, B, C and D</strong> — a reason the land produced less than
						$1,000: out of production, being developed for agriculture, a crop that is not
						harvested every year, or sales of at least $500 on under 5 acres. These open only
						when total production is below $1,000, and exactly one must be chosen. The test uses
						the <strong>production value</strong>, not Farm Income, so editing the income figure
						never enables or disables them.</li>
						<li><strong>Non-farm and Mineral Income</strong> — income from the property that did
						not come from agriculture.</li>
						<li><strong>Farm Income</strong> — starts as a suggestion equal to the Total
						Production Value and can be typed over, including with <strong>0</strong>. On a farm
						that consumes most of what it grows, the value produced and the income earned are
						different figures. Clearing the field returns it to following the total.</li>
						<li><strong>Question 2a</strong> (is at least 50% of income agricultural) — answered
						automatically from the two income figures and locked, so the answer cannot contradict
						the numbers beside it. Where no income at all is recorded there is no proportion to
						take, and the boxes unlock for a manual answer.</li>
						<li><strong>Question 3</strong> (corporate farming) and <strong>Question 4</strong>
						(perpetual conservation easement) — both start blank and must be answered.</li>
					</ul>
					<p style={p}>
						Click <strong>Save Qualifications</strong> after making changes. Save stays greyed out
						until every item above has an answer, and a line above the button names what is still
						outstanding. The Farm Income field has a <strong style={{ backgroundColor: "lightgreen", color: "#333", padding: "0 4px", borderRadius: "2px" }}>light green background</strong> — press <kbd style={kbd}>F1</kbd> while in it for
						full instructions.
					</p>
					<p style={p}>
						Applications entered before version 1.13 showed <strong>No</strong> on questions 2a, 3
						and 4 whether or not anyone had answered them, because the form had no way to record
						an unanswered question. Those answers have been cleared: a blank pair means the
						question is genuinely unanswered and needs a deputy's answer, not that an answer was
						lost. Only a recorded <strong>Yes</strong> was kept.
					</p>

					<h4 style={h4}>Progress indicators</h4>
					<p style={p}>
						A row of coloured dots at the top of the application shows the completion status
						of each section:
					</p>
					<ul style={ul}>
						<li><strong style={{ color: "#90ee90" }}>Green</strong> — the section has been saved with data this fiscal year.</li>
						<li><strong style={{ color: "#ffd700" }}>Yellow</strong> — the section contains data carried over from a roll forward that has not yet been reviewed and saved for the new year.</li>
						<li><strong style={{ color: "gray" }}>Grey</strong> — the section is empty or has no data applicable to this year.</li>
					</ul>
					<p style={p}>
						All sections should be green or intentionally grey before the application is
						considered complete.
					</p>
				</fieldset>
			</fieldset>

			{/* ── Reports ───────────────────────────────────────────────────────── */}
			<fieldset style={{ ...fs, marginBottom: 0 }}>
				<legend>Reports</legend>
				<p style={p}>
					The Reports module provides pre-built report templates for common assessor office needs.
					Reports are grouped into <strong>Dog</strong> and <strong>Farm</strong> categories,
					selectable from the Reports menu. Select a report from the list on the left side of the
					page. Bookmarked reports appear at the top of the list for quick access — click the star
					icon to bookmark or un-bookmark a report.
				</p>
				<p style={p}>
					Some reports require date or search parameters. Enter the required values in the parameter
					fields and click <strong>Run Report</strong>. The report output appears in the panel on
					the right. Click <strong>Print</strong> to send the report to your printer or save it as a PDF.
				</p>
				<h4 style={h4}>Available reports</h4>
				<ul style={ul}>
					<li><strong>Dog License Receipt</strong> — prints the receipt for a specific transaction.
					Opens to the most recent receipt by default; enter a receipt number or taxpayer last name
					to retrieve a specific one.</li>
					<li><strong>Dog License Report</strong> — lists all dog licenses issued for the selected
					tax year, grouped by owner. Includes tag number, tag status, breed, and fee paid.
					Filter by taxpayer last name to view a specific owner's records.</li>
					<li><strong>Daily Deputy Tags</strong> — end-of-day reconciliation for a deputy,
					showing all tags issued on a specific date grouped by owner. Includes tag status for
					each dog.</li>
					<li><strong>Kennel Certificate</strong> — official kennel registration certificate
					for a registered kennel owner. Printed automatically at Save &amp; Print when a kennel
					registration is processed.</li>
					<li><strong>Deputy Financial Month</strong> — a monthly financial summary by deputy
					covering dog tags, kennel registrations, and sheep/goat registrations with totals.
					Enter the <strong>Year</strong> and <strong>Month</strong> to run it. (This report was
					previously titled the deputy audit.)</li>
					<li><strong>Deputy Financial Daily</strong> — the same monthly figures broken down day
					by day, with per-day subtotals, a vicious-fee column, and a month total. Enter the
					<strong> Year</strong> and <strong>Month</strong> to run it.</li>
					<li><strong>Farm Owner List</strong> — lists farm operation names and owners who filed
					an application for a given fiscal year, with address, city, email, phone, and status.
					Select the <strong>Fiscal Year</strong> and a <strong>Status</strong> filter (Show All,
					Pending, Granted, Denied, or Withdrawn) to run it.</li>
					<li><strong>Farm Use Valuation Application</strong> — the WV State Farm Census and Farm
					Use Valuation Application form. Select the <strong>Fiscal Year</strong> and a
					<strong> Status</strong> filter (All, Granted, Denied, Pending, or Withdrawn, defaulting
					to Granted), and optionally an <strong>Owner Name</strong> to print a single owner's
					form.</li>
					<li><strong>Other reports</strong> — additional reports for sheep and goat registrations
					are available in the report list.</li>
				</ul>
			</fieldset>

			<div style={{ height: "3rem" }} />
		</main>
	)
}

export default Help
